"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@ocr-monorepo/ui/components/button";
import { Input } from "@ocr-monorepo/ui/components/input";
import { Label } from "@ocr-monorepo/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ocr-monorepo/ui/components/select";
import { Textarea } from "@ocr-monorepo/ui/components/textarea";
import { AlertCircle, CheckCircle2, LoaderCircle, Send } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Controller, useForm } from "react-hook-form";

import { TurnstileWidget } from "@/components/turnstile-widget";
import { captureGrowthEvent } from "@/lib/analytics";
import {
  contactFormSchema,
  contactTopicLabels,
  contactTopics,
  type ContactFormValues,
} from "@/lib/contact";

type SubmissionState =
  | { kind: "idle" }
  | { kind: "success"; submissionId: string }
  | { kind: "saved"; message: string }
  | { kind: "error"; message: string };

const defaultValues: ContactFormValues = {
  name: "",
  email: "",
  topic: "technical_support",
  subject: "",
  message: "",
  turnstileToken: "",
  website: "",
};

export function ContactForm() {
  const [submissionState, setSubmissionState] = useState<SubmissionState>({
    kind: "idle",
  });
  const [turnstileKey, setTurnstileKey] = useState(0);
  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues,
  });

  const resetTurnstile = () => {
    setValue("turnstileToken", "", { shouldValidate: false });
    setTurnstileKey((current) => current + 1);
  };

  const submit = handleSubmit(async (values) => {
    setSubmissionState({ kind: "idle" });
    captureGrowthEvent("contact_form_started", { topic: values.topic });

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const result = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        saved?: boolean;
        submissionId?: string;
      };

      if (!response.ok) {
        if (result.saved) {
          setSubmissionState({
            kind: "saved",
            message:
              result.error ??
              "Your message was saved, but the email notification is delayed.",
          });
          captureGrowthEvent("contact_form_failed", {
            topic: values.topic,
            reason: "email_delivery",
            saved: true,
          });
        } else {
          setSubmissionState({
            kind: "error",
            message:
              result.error ??
              "We could not send your message. Please try again.",
          });
          captureGrowthEvent("contact_form_failed", {
            topic: values.topic,
            reason: `http_${response.status}`,
            saved: false,
          });
        }
        resetTurnstile();
        return;
      }

      setSubmissionState({
        kind: "success",
        submissionId: result.submissionId ?? "received",
      });
      captureGrowthEvent("contact_form_submitted", { topic: values.topic });
      reset(defaultValues);
      setTurnstileKey((current) => current + 1);
    } catch {
      setSubmissionState({
        kind: "error",
        message:
          "We could not reach the server. Check your connection and try again.",
      });
      captureGrowthEvent("contact_form_failed", {
        topic: values.topic,
        reason: "network",
        saved: false,
      });
      resetTurnstile();
    }
  });

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          label="Your name"
          error={errors.name?.message}
          htmlFor="contact-name"
        >
          <Input
            id="contact-name"
            autoComplete="name"
            placeholder="Jane Smith"
            aria-invalid={Boolean(errors.name)}
            {...register("name")}
          />
        </Field>
        <Field
          label="Work email"
          error={errors.email?.message}
          htmlFor="contact-email"
        >
          <Input
            id="contact-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="jane@company.com"
            aria-invalid={Boolean(errors.email)}
            {...register("email")}
          />
        </Field>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          label="What can we help with?"
          error={errors.topic?.message}
          htmlFor="contact-topic"
        >
          <Controller
            name="topic"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  id="contact-topic"
                  className="w-full"
                  aria-invalid={Boolean(errors.topic)}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {contactTopics.map((topic) => (
                    <SelectItem key={topic} value={topic}>
                      {contactTopicLabels[topic]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>
        <Field
          label="Subject"
          error={errors.subject?.message}
          htmlFor="contact-subject"
        >
          <Input
            id="contact-subject"
            placeholder="A quick summary"
            aria-invalid={Boolean(errors.subject)}
            {...register("subject")}
          />
        </Field>
      </div>

      <Field
        label="How can we help?"
        error={errors.message?.message}
        htmlFor="contact-message"
      >
        <Textarea
          id="contact-message"
          className="min-h-40 resize-y"
          placeholder="Tell us what you are trying to do, what happened, and any error message you saw."
          aria-invalid={Boolean(errors.message)}
          {...register("message")}
        />
      </Field>

      <div className="absolute -left-[9999px]" aria-hidden="true">
        <Label htmlFor="contact-website">Website</Label>
        <Input
          id="contact-website"
          tabIndex={-1}
          autoComplete="off"
          {...register("website")}
        />
      </div>

      <div className="rounded-sm border border-border bg-muted/30 p-3">
        <TurnstileWidget
          key={turnstileKey}
          action="contact-form"
          size="flexible"
          onSuccess={(token) =>
            setValue("turnstileToken", token, { shouldValidate: true })
          }
          onExpire={resetTurnstile}
          onError={() =>
            setValue("turnstileToken", "", { shouldValidate: false })
          }
        />
        {errors.turnstileToken?.message ? (
          <p className="mt-2 text-center text-xs text-destructive">
            {errors.turnstileToken.message}
          </p>
        ) : null}
      </div>

      <div aria-live="polite">
        {submissionState.kind === "success" ? (
          <StatusMessage kind="success">
            Message received. We sent it to the team and saved a copy under
            reference{" "}
            <span className="font-mono">{submissionState.submissionId}</span>.
          </StatusMessage>
        ) : null}
        {submissionState.kind === "saved" ? (
          <StatusMessage kind="warning">
            {submissionState.message}
          </StatusMessage>
        ) : null}
        {submissionState.kind === "error" ? (
          <StatusMessage kind="error">{submissionState.message}</StatusMessage>
        ) : null}
      </div>

      <Button
        type="submit"
        size="marketing"
        className="w-full sm:w-auto"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <LoaderCircle className="animate-spin" /> Sending securely…
          </>
        ) : (
          <>
            Send message <Send />
          </>
        )}
      </Button>
      <p className="text-xs leading-relaxed text-muted-foreground">
        Your details are used only to respond to this request. Please do not
        include passwords, API keys, or sensitive document contents.
      </p>
    </form>
  );
}

function Field({
  label,
  error,
  htmlFor,
  children,
}: {
  label: string;
  error?: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function StatusMessage({
  kind,
  children,
}: {
  kind: "success" | "warning" | "error";
  children: ReactNode;
}) {
  const success = kind === "success";
  const warning = kind === "warning";
  const Icon = success ? CheckCircle2 : AlertCircle;

  return (
    <div
      role={success ? "status" : "alert"}
      className={
        success
          ? "flex items-start gap-3 rounded-sm border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-800 dark:text-emerald-200"
          : warning
            ? "flex items-start gap-3 rounded-sm border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-200"
            : "flex items-start gap-3 rounded-sm border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive"
      }
    >
      <Icon className="mt-0.5 size-4 shrink-0" />
      <p>{children}</p>
    </div>
  );
}
